import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import {
  computeAvailableSlots,
  isDateBookable,
  MONTHS,
  SHORT_DAYS,
  DAY_KEYS,
  formatDateFr,
  toDateStr,
  type BookingService,
  type BookingAvailability,
  type BookingBreak,
  type BookingHoliday,
  type BookingAppointment,
} from '@/lib/booking';
import { pushNotification } from '@/lib/notifications';

interface Props {
  serviceId: number;
  serviceName: string;
  onClose: () => void;
  onViewDetail?: () => void;
  /** Créneau déjà choisi depuis le panneau « Voir les disponibilités ». */
  initialDate?: string | null;
  initialTime?: string | null;
}

/**
 * QuickBookingModal — réservation directe depuis la fenêtre de détail de l'IA.
 * Le service est pré-rempli, l'utilisateur choisit une date + un créneau,
 * puis le rendez-vous est créé en un clic (avec ses coordonnées de compte).
 */
export default function QuickBookingModal({ serviceId, serviceName, onClose, onViewDetail, initialDate, initialTime }: Props) {
  const { user } = useAuth();
  const [service, setService] = useState<BookingService | null>(null);
  const [ownerEmail, setOwnerEmail] = useState('');
  const [availabilities, setAvailabilities] = useState<BookingAvailability[]>([]);
  const [breaks, setBreaks] = useState<BookingBreak[]>([]);
  const [holidays, setHolidays] = useState<BookingHoliday[]>([]);
  const [appointments, setAppointments] = useState<BookingAppointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const today = useMemo(() => new Date(), []);
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [phone, setPhone] = useState(user?.telephone || '');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!initialDate) return;
    setSelectedDate(initialDate);
    setSelectedTime(initialTime || null);
    try {
      const d = new Date(`${initialDate}T00:00:00`);
      setCalMonth(d.getMonth());
      setCalYear(d.getFullYear());
    } catch {
      /* date invalide : on garde le mois courant */
    }
  }, [initialDate, initialTime]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const { data: svc, error: svcErr } = await supabase
          .from('booking_services')
          .select('*')
          .eq('id', serviceId)
          .eq('statut', 1)
          .maybeSingle();
        if (cancelled) return;
        if (svcErr || !svc) { setLoadError('Service indisponible pour la réservation.'); setLoading(false); return; }
        setService(svc as BookingService);

        const [ownerRes, avRes, brRes, hoRes, apRes, avGlobal, brGlobal, hoGlobal] = await Promise.all([
          supabase.from('users').select('email').eq('id', (svc as BookingService).owner).maybeSingle(),
          supabase.from('booking_availabilities').select('*').eq('service_id', (svc as BookingService).id),
          supabase.from('booking_breaks').select('*').eq('service_id', (svc as BookingService).id),
          supabase.from('booking_holidays').select('*').eq('service_id', (svc as BookingService).id),
          supabase.from('booking_appointments').select('id, date, time').eq('service_id', (svc as BookingService).id).neq('status', 'cancelled'),
          supabase.from('booking_availabilities').select('*').eq('service_id', 0),
          supabase.from('booking_breaks').select('*').eq('service_id', 0),
          supabase.from('booking_holidays').select('*').eq('service_id', 0),
        ]);
        if (cancelled) return;
        if (ownerRes.data?.email) setOwnerEmail(ownerRes.data.email);

        const mergedAvail = new Map<string, BookingAvailability>();
        (avGlobal.data || []).forEach((a) => mergedAvail.set((a as BookingAvailability).day_of_week, a as BookingAvailability));
        (avRes.data || []).forEach((a) => mergedAvail.set((a as BookingAvailability).day_of_week, a as BookingAvailability));

        const mergedBr = new Map<string, BookingBreak>();
        (brGlobal.data || []).forEach((b) => mergedBr.set(`${(b as BookingBreak).day_of_week}-${(b as BookingBreak).start_time}-${(b as BookingBreak).end_time}`, b as BookingBreak));
        (brRes.data || []).forEach((b) => mergedBr.set(`${(b as BookingBreak).day_of_week}-${(b as BookingBreak).start_time}-${(b as BookingBreak).end_time}`, b as BookingBreak));

        const mergedHol = new Map<string, BookingHoliday>();
        (hoGlobal.data || []).forEach((h) => mergedHol.set(`${(h as BookingHoliday).date_from}-${(h as BookingHoliday).date_to}`, h as BookingHoliday));
        (hoRes.data || []).forEach((h) => mergedHol.set(`${(h as BookingHoliday).date_from}-${(h as BookingHoliday).date_to}`, h as BookingHoliday));

        setAvailabilities(Array.from(mergedAvail.values()));
        setBreaks(Array.from(mergedBr.values()));
        setHolidays(Array.from(mergedHol.values()));
        setAppointments((apRes.data || []) as BookingAppointment[]);
      } catch {
        if (!cancelled) setLoadError('Impossible de charger les disponibilités.');
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [serviceId]);

  // Calendrier
  const firstOfMonth = new Date(calYear, calMonth, 1);
  const lastOfMonth = new Date(calYear, calMonth + 1, 0);
  const startPad = firstOfMonth.getDay() === 0 ? 6 : firstOfMonth.getDay() - 1;
  const monthDays: (number | null)[] = [];
  for (let i = 0; i < startPad; i++) monthDays.push(null);
  for (let i = 1; i <= lastOfMonth.getDate(); i++) monthDays.push(i);

  const availableSlots = useMemo(
    () => (selectedDate && service ? computeAvailableSlots(selectedDate, service, availabilities, breaks, appointments) : []),
    [selectedDate, service, availabilities, breaks, appointments],
  );

  const handleSubmit = useCallback(async () => {
    if (!service || !selectedDate || !selectedTime || !user) return;
    if (!user.name?.trim()) { setFormError('Votre nom de compte est requis.'); return; }
    if (!user.email?.trim()) { setFormError('Votre email de compte est requis.'); return; }

    setSubmitting(true);
    setFormError('');
    try {
      const { data: appt, error: insertErr } = await supabase
        .from('booking_appointments')
        .insert({
          service_id: service.id,
          fullname: user.name.trim(),
          email: user.email.trim(),
          phone: phone.trim(),
          message: message.trim(),
          date: selectedDate,
          time: selectedTime,
          status: 'confirmed',
        })
        .select('id')
        .single();

      if (insertErr) throw insertErr;

      // 🔔 Cloche + notification PWA du prestataire (avec son d'alerte).
      void pushNotification({
        userId: service.owner,
        type: 'booking',
        message: `Nouvelle réservation : ${service.service_name}, le ${selectedDate} à ${selectedTime} (${user.name}).`,
      });

      if (ownerEmail) {
        supabase.functions.invoke('send-booking-notification', {
          body: {
            to: ownerEmail,
            type: 'new_booking',
            service_name: service.service_name,
            customer_name: user.name.trim(),
            customer_email: user.email.trim(),
            customer_phone: phone.trim(),
            customer_message: message.trim(),
            date: selectedDate,
            time: selectedTime,
            duration: service.duration,
          },
        }).catch(() => { /* l'email ne doit pas bloquer la réservation */ });
      }

      setAppointments((prev) => [...prev, { id: (appt?.id as number) || 0, date: selectedDate, time: selectedTime }]);
      setSuccess(true);
    } catch {
      setFormError('La réservation a échoué. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  }, [service, selectedDate, selectedTime, user, phone, message, ownerEmail]);

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[60]" onClick={submitting ? undefined : onClose} />
      <div className="fixed z-[60] inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center sm:p-4">
        <div className="w-full sm:max-w-lg bg-background-50 rounded-t-2xl sm:rounded-2xl border border-background-200/70 max-h-[94vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70 flex-shrink-0">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
                <i className="ri-calendar-check-line text-lg"></i>
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-foreground-950 truncate">Réserver : {serviceName}</h3>
                <p className="text-[11px] text-foreground-500 truncate">
                  {service ? `${service.duration} min` : 'Chargement...'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={submitting}
              className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer flex-shrink-0 disabled:opacity-40"
            >
              <i className="ri-close-line text-foreground-500"></i>
            </button>
          </div>

          <div className="p-5 overflow-y-auto">
            {success ? (
              <div className="py-10 flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center mb-4">
                  <i className="ri-check-line text-3xl text-accent-600"></i>
                </div>
                <h4 className="text-base font-bold font-heading text-foreground-950 mb-1">Rendez-vous confirmé !</h4>
                <p className="text-sm text-foreground-500">
                  {formatDateFr(selectedDate!)} à {selectedTime}
                </p>
                <p className="text-xs text-foreground-400 mt-1">{service?.service_name} ({service?.duration} min)</p>
              </div>
            ) : loading ? (
              <div className="py-14 flex items-center justify-center">
                <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
              </div>
            ) : loadError ? (
              <div className="py-10 flex flex-col items-center text-center">
                <i className="ri-error-warning-line text-3xl text-red-400 mb-3"></i>
                <p className="text-sm text-foreground-600">{loadError}</p>
                {onViewDetail && (
                  <button onClick={onViewDetail} className="mt-4 px-5 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap">
                    Voir la page du service
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {/* Calendar */}
                <div className="border border-background-200/70 rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between p-3 border-b border-background-200/70">
                    <button
                      onClick={() => { if (calMonth === 0) { setCalMonth(11); setCalYear(calYear - 1); } else setCalMonth(calMonth - 1); }}
                      className="w-7 h-7 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200/70 cursor-pointer transition-colors"
                    >
                      <i className="ri-arrow-left-s-line text-foreground-600"></i>
                    </button>
                    <h4 className="text-sm font-semibold text-foreground-800">{MONTHS[calMonth]} {calYear}</h4>
                    <button
                      onClick={() => { if (calMonth === 11) { setCalMonth(0); setCalYear(calYear + 1); } else setCalMonth(calMonth + 1); }}
                      className="w-7 h-7 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200/70 cursor-pointer transition-colors"
                    >
                      <i className="ri-arrow-right-s-line text-foreground-600"></i>
                    </button>
                  </div>
                  <div className="grid grid-cols-7 border-b border-background-200/70">
                    {SHORT_DAYS.map((d) => (
                      <div key={d} className="py-1.5 text-center text-[10px] font-medium text-foreground-400">{d}</div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 p-1.5 gap-1">
                    {monthDays.map((day, i) => {
                      if (day === null) return <div key={`e-${i}`} className="aspect-square" />;
                      const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                      const isToday = dateStr === toDateStr(today);
                      const available = service ? isDateBookable(dateStr, service, availabilities, holidays, breaks, appointments) : false;
                      const isSelected = dateStr === selectedDate;
                      return (
                        <button
                          key={dateStr}
                          disabled={!available}
                          onClick={() => { setSelectedDate(dateStr); setSelectedTime(null); setFormError(''); }}
                          className={`aspect-square flex items-center justify-center rounded-md text-xs cursor-pointer transition-all ${
                            isSelected ? 'bg-primary-500 text-background-50 font-bold'
                              : isToday ? 'bg-primary-50 text-primary-700 font-semibold'
                              : available ? 'text-foreground-700 hover:bg-accent-50 hover:text-accent-700'
                              : 'text-foreground-300 cursor-not-allowed'
                          }`}
                        >
                          {day}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Slots */}
                {selectedDate && (
                  <div>
                    <p className="text-xs font-medium text-foreground-600 mb-2">{formatDateFr(selectedDate)}</p>
                    {availableSlots.length === 0 ? (
                      <p className="text-xs text-foreground-400 bg-background-100 rounded-lg px-3 py-3 text-center">
                        Aucun créneau disponible à cette date.
                      </p>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {availableSlots.map((slot) => (
                          <button
                            key={slot}
                            onClick={() => { setSelectedTime(slot); setFormError(''); }}
                            className={`py-2 rounded-lg text-sm font-medium cursor-pointer transition-all whitespace-nowrap ${
                              selectedTime === slot
                                ? 'bg-primary-500 text-background-50'
                                : 'bg-background-50 border border-background-200/70 text-foreground-700 hover:border-accent-300 hover:text-accent-700'
                            }`}
                          >
                            {slot}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {selectedDate && selectedTime && (
                  <div className="space-y-3 pt-1">
                    <div>
                      <label className="block text-xs font-semibold text-foreground-600 mb-1">Téléphone (optionnel)</label>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+212 6 12 34 56 78"
                        className="w-full h-11 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-foreground-600 mb-1">Message (optionnel)</label>
                      <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={2}
                        maxLength={500}
                        placeholder="Précisez l'objet du rendez-vous..."
                        className="w-full px-3 py-2 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 resize-none"
                      />
                    </div>
                  </div>
                )}

                {formError && (
                  <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{formError}</p>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          {!success && !loading && !loadError && (
            <div className="px-5 py-4 border-t border-background-200/70 flex flex-col sm:flex-row gap-2.5 flex-shrink-0">
              <button
                onClick={onClose}
                disabled={submitting}
                className="w-full sm:w-auto h-12 px-5 rounded-full bg-background-100 border border-background-200/70 text-sm font-medium text-foreground-600 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40"
              >
                Plus tard
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || !selectedDate || !selectedTime || !user}
                className="flex-1 h-12 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <><i className="ri-loader-4-line animate-spin"></i>Réservation...</>
                ) : (
                  <><i className="ri-calendar-check-line"></i>{selectedTime ? `Confirmer à ${selectedTime}` : 'Choisir un créneau'}</>
                )}
              </button>
            </div>
          )}

          {success && (
            <div className="px-5 py-4 border-t border-background-200/70 flex-shrink-0">
              <button
                onClick={onClose}
                className="w-full h-12 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                Terminer
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}